import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ExternalLink, IconSpinner, LayoutSingleColumn, Page } from '../../components';
import { useIntl } from '../../util/reactIntl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getAffiliates } from '../../util/strapiApi';

import css from './OurAffiliatesPage.module.css';

const { bool } = PropTypes;
const FALLBACK_AFFILIATE_NAME = 'Affiliate';

const getAffiliateHost = value => {
  try {
    const hostname = new URL(value).hostname || '';
    return hostname.replace(/^www\./, '');
  } catch (error) {
    return '';
  }
};

export const OurAffiliatesPageComponent = props => {
  const { scrollingDisabled, viewport } = props;
  const intl = useIntl();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setHasError(false);

      try {
        const response = await getAffiliates();
        if (!active) return;
        setAffiliates(Array.isArray(response) ? response : []);
      } catch (error) {
        if (!active) return;
        setHasError(true);
        setAffiliates([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, []);

  const pageTitle = intl.formatMessage({ id: 'OurAffiliatesPage.title' });

  const loadingState = (
    <div className={css.centerState}>
      <IconSpinner />
      <p>{intl.formatMessage({ id: 'OurAffiliatesPage.loading' })}</p>
    </div>
  );

  const errorState = (
    <div className={css.centerState}>
      <p>{intl.formatMessage({ id: 'OurAffiliatesPage.error' })}</p>
    </div>
  );

  const emptyState = (
    <div className={css.centerState}>
      <p>{intl.formatMessage({ id: 'OurAffiliatesPage.empty' })}</p>
    </div>
  );

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={pageTitle}
      title={pageTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: pageTitle,
        description: pageTitle,
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer viewport={viewport} />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <header className={css.hero}>
            <h1>{pageTitle}</h1>
            <p>{intl.formatMessage({ id: 'OurAffiliatesPage.intro' })}</p>
          </header>

          {loading ? loadingState : null}
          {!loading && hasError ? errorState : null}
          {!loading && !hasError && affiliates.length === 0 ? emptyState : null}

          {!loading && !hasError && affiliates.length > 0 ? (
            <section className={css.grid}>
              {affiliates.map(affiliate => {
                const visitLabel =
                  affiliate?.buttonText?.trim() ||
                  intl.formatMessage({ id: 'OurAffiliatesPage.visitButton' });
                const displayName = affiliate?.title || FALLBACK_AFFILIATE_NAME;
                const hostName = affiliate?.url ? getAffiliateHost(affiliate.url) : '';

                return (
                  <article
                    key={affiliate.id || `${affiliate.title}-${affiliate.url}`}
                    className={css.card}
                  >
                    <div className={css.logoWrap}>
                      {affiliate.logoUrl ? (
                        <img
                          src={affiliate.logoUrl}
                          alt={displayName}
                          className={css.logo}
                          loading="lazy"
                        />
                      ) : (
                        <span className={css.logoPlaceholder}>{displayName}</span>
                      )}
                    </div>

                    <div className={css.content}>
                      <h2 className={css.title}>{displayName}</h2>
                      {affiliate.tagLine ? (
                        <p className={css.tagline}>{affiliate.tagLine}</p>
                      ) : null}
                      {hostName ? <p className={css.urlText}>{hostName}</p> : null}
                    </div>

                    <div className={css.ctaWrap}>
                      {affiliate.url ? (
                        <ExternalLink href={affiliate.url} className={css.ctaButton}>
                          {visitLabel}
                        </ExternalLink>
                      ) : (
                        <span
                          className={`${css.ctaButton} ${css.ctaButtonDisabled}`}
                          aria-disabled="true"
                        >
                          {visitLabel}
                        </span>
                      )}
                    </div>
                  </article>
                );
              })}
            </section>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

OurAffiliatesPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const OurAffiliatesPage = compose(
  connect(
    mapStateToProps,
    null
  ),
  withViewport
)(OurAffiliatesPageComponent);

export default OurAffiliatesPage;
